import React, { useContext } from 'react';
import { InstructionStepDefinition, SurveyDefinition, SurveyStepDefinition } from '../../../types/Survey';
import { ManagedResultsLookup } from '../../../helpers/surveyPredicates';
import { StepResultModel } from '../../../types/SurveyResult';
import { getContentItemSettings, getFormStyle, getIconImageStyles, getNextButtonStyles, getTextStyles } from '../Util/surveyStyles';
import { isLastInstructionStep, getVisibleFormItems, buildStepResult, resolvePresentedDate } from '../Util/surveyStepUtils';
import { SurveyStepContext } from '../Util/surveyStepContext';
import { useFormItemResults } from '../Util/useFormItemResults';
import IconImage from '../Components/IconImage/IconImage';
import SurveyMarkdown from '../Components/SurveyMarkdown/SurveyMarkdown';
import SurveyForm from '../Components/SurveyForm/SurveyForm';
import SurveyNextButton from '../Components/SurveyNextButton/SurveyNextButton';

export interface InstructionStepProps {
    step: InstructionStepDefinition;
    surveyDefinition: SurveyDefinition;
    nextStep: SurveyStepDefinition | null;
    visitedStepIdentifiers: string[];
    managedResults: ManagedResultsLookup;
    predicateExpressionEvaluator: (expression: string) => boolean;
    onContinue: (stepResult: StepResultModel) => void;
    onBack: () => void;
    onResultChange: (stepResult: StepResultModel) => void;
    presentedDate?: string;
    linkSurvey?: boolean;
}

export default function InstructionStep({
    step,
    surveyDefinition,
    nextStep,
    visitedStepIdentifiers,
    managedResults,
    predicateExpressionEvaluator,
    onContinue,
    onResultChange,
    presentedDate: presentedDateProp,
    linkSurvey = false,
}: InstructionStepProps) {
    const { mode } = useContext(SurveyStepContext) ?? { mode: 'Survey' as const };
    const presentedDate = resolvePresentedDate(presentedDateProp);
    const last = nextStep == null;
    // In Design mode never show "Get Started" — matches Angular's editMode guard
    const lastInstruction = mode !== 'Design' && isLastInstructionStep(visitedStepIdentifiers, surveyDefinition.steps, nextStep);
    // In Design mode, show all form items regardless of any hidePredicate type
    const visibleFormItems = getVisibleFormItems(step.formItems ?? [], managedResults, predicateExpressionEvaluator, mode === 'Design');
    // hideNextButton only applies to link/recruitment surveys or in Design/edit mode,
    // so participants in normal task-based surveys are never trapped on the step.
    // Matches Angular's InstructionStepManager.hideNextButton guard.
    const hideNextButton = step.hideNextButton && (linkSurvey || mode === 'Design');

    const { visibleResults, handleResultChange } = useFormItemResults(
        step.identifier ?? '',
        step.formItems ?? [],
        managedResults,
        visibleFormItems,
        presentedDate,
        onResultChange
    );

    function handleContinue() {
        onContinue(buildStepResult(step.identifier ?? '', presentedDate, visibleResults));
    }

    return (
        <>
            <div className="current-step instruction-step">
                <div className="step-content">
                    <IconImage iconImage={step.iconImage} iconImageStyles={getIconImageStyles(surveyDefinition, step)} />
                    <div className="instruction-step-text">
                        {step.title && (
                            <div className="step-title" style={getTextStyles('title', surveyDefinition, step, true)}>
                                <SurveyMarkdown text={step.title} inline={true} />
                            </div>
                        )}
                        {step.text && (
                            <div className="step-text" style={getTextStyles('text', surveyDefinition, step, true)}>
                                <SurveyMarkdown text={step.text} />
                            </div>
                        )}
                        {step.detailText && (
                            <div className="step-text" style={getTextStyles('detailText', surveyDefinition, step, true)}>
                                <SurveyMarkdown text={step.detailText} />
                            </div>
                        )}
                    </div>
                    {step.image && (
                        <div className="step-image-main">
                            <img src={step.image} alt={step.imageAltText} />
                        </div>
                    )}
                    <SurveyForm
                        formItems={visibleFormItems}
                        results={visibleResults}
                        onResultChange={handleResultChange}
                        onEnterPressed={handleContinue}
                        variant={getFormStyle(surveyDefinition, step)}
                        contentItemSettings={getContentItemSettings(surveyDefinition, step)}
                    />                   
                    {!hideNextButton && (
                        <SurveyNextButton
                            nextButtonText={step.nextButtonText}
                            isLastStep={last}
                            isLastInstructionStep={lastInstruction}
                            style={getNextButtonStyles(surveyDefinition, step, true, true) as React.CSSProperties}
                            onClick={handleContinue}
                        />
                    )}
                </div>
            </div>
            {step.footnote && (
                <div className="step-footnote" style={getTextStyles('footnote', surveyDefinition, step, true)}>
                    <SurveyMarkdown text={step.footnote} inline={true} />
                </div>
            )}
        </>
    );
}
